import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../../hooks/useApi';
import { formatCurrency } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, ChevronsUpDown, Check, Newspaper, Globe, Package as PackageIcon, Loader2, Tag } from 'lucide-react';

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
let _uidCounter = 0;
const newUid = () => `li_${Date.now()}_${_uidCounter++}`;

const blankItem = (defaultTax) => ({
  _uid: newUid(),
  category: 'digital_edition',
  product_type: '',
  edition_ids: [],
  rate: 0,
  quantity: 1,
  is_taxable: false,
  tax_rate: defaultTax || 16,
  needs_factura: false,
  digital_start_date: '',
  digital_end_date: '',
  repeat_every_weeks: 1,
  special_instructions: '',
  package_id: null,
  package_name: null,
});

const OrderModal = ({ isOpen, onClose, advertisers, editions, productTypes, salesReps, settings, onSave }) => {
  const defaultTax = settings?.default_tax_rate ?? 16;
  const [advertiserId, setAdvertiserId] = useState('');
  const [advOpen, setAdvOpen] = useState(false);
  const [salesRepId, setSalesRepId] = useState('none');
  const [items, setItems] = useState([]);
  const [discountType, setDiscountType] = useState('none');
  const [discountValue, setDiscountValue] = useState(0);
  const [notes, setNotes] = useState('');
  const [orderTaxable, setOrderTaxable] = useState(false);
  const [orderTaxRate, setOrderTaxRate] = useState(defaultTax);
  const [orderFactura, setOrderFactura] = useState(false);
  const [packages, setPackages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setAdvertiserId(''); setSalesRepId('none'); setItems([blankItem(defaultTax)]);
      setDiscountType('none'); setDiscountValue(0); setNotes(''); setError('');
      setOrderTaxable(false); setOrderTaxRate(defaultTax); setOrderFactura(false);
      api.get('/packages').then(setPackages).catch(() => setPackages([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const categories = useMemo(() => {
    const set = new Set((productTypes || []).map(p => p.category).filter(Boolean));
    const list = [];
    if (set.has('digital_edition')) list.push({ value: 'digital_edition', label: 'Print (Edition)' });
    if (set.has('website')) list.push({ value: 'website', label: 'Digital / Web' });
    Array.from(set).filter(c => c !== 'digital_edition' && c !== 'website').forEach(c => list.push({ value: c, label: c }));
    return list.length ? list : [{ value: 'digital_edition', label: 'Print (Edition)' }, { value: 'website', label: 'Digital / Web' }];
  }, [productTypes]);

  const ptByName = (name) => (productTypes || []).find(p => p.name === name);

  const updateItem = (uid, patch) => setItems(prev => prev.map(it => it._uid === uid ? { ...it, ...patch } : it));
  const removeItem = (uid) => setItems(prev => prev.filter(it => it._uid !== uid));
  const addItem = () => setItems(prev => [...prev, blankItem(defaultTax)]);

  const onCategoryChange = (uid, category) => {
    updateItem(uid, { category, product_type: '', rate: 0, edition_ids: [], digital_start_date: '', digital_end_date: '' });
  };
  const onProductChange = (uid, name) => {
    const pt = ptByName(name);
    updateItem(uid, { product_type: name, rate: pt?.rate ?? 0 });
  };

  const addPackage = (pkgId) => {
    const pkg = packages.find(p => p.id === pkgId);
    if (!pkg) return;
    const lines = (pkg.items || []).map(it => {
      const pt = ptByName(it.product_type);
      const qty = Math.max(1, it.quantity || 1);
      const baseRate = it.rate != null ? it.rate : (pt?.rate ?? 0);
      return { ...blankItem(defaultTax), category: it.category || pt?.category || 'website', product_type: it.product_type, quantity: qty, rate: baseRate, package_id: pkg.id, package_name: pkg.name, _weight: baseRate * qty };
    });
    if (pkg.bundle_price != null) {
      const sumW = lines.reduce((s, l) => s + l._weight, 0);
      if (sumW > 0) lines.forEach(l => { const share = pkg.bundle_price * (l._weight / sumW); l.rate = round2(share / l.quantity); });
    }
    setItems(prev => [...prev, ...lines.map(({ _weight, ...l }) => l)]);
  };

  // Per-line unit count (how many bookings this line yields)
  const lineCount = (it) => {
    if (it.category === 'digital_edition') return Math.max(1, (it.edition_ids?.length || 0)) * Math.max(1, it.quantity || 1);
    return Math.max(1, it.quantity || 1);
  };
  const lineSubtotal = (it) => round2((it.rate || 0) * lineCount(it));

  const subtotal = useMemo(() => round2(items.reduce((s, it) => s + lineSubtotal(it), 0)), [items]);
  const discountAmount = useMemo(() => {
    if (discountType === 'percent') return round2(subtotal * (Number(discountValue) || 0) / 100);
    if (discountType === 'amount') return round2(Math.min(Number(discountValue) || 0, subtotal));
    return 0;
  }, [discountType, discountValue, subtotal]);
  const total = round2(subtotal - discountAmount);
  const taxAmount = orderTaxable ? round2(total * (Number(orderTaxRate) || 0) / 100) : 0;
  const grandTotal = round2(total + taxAmount);

  const buildScheduleType = (it) => {
    if (it.category !== 'website') return 'none';
    const pt = ptByName(it.product_type);
    const single = !!pt?.single_day_product;
    if ((it.quantity || 1) <= 1) return 'none';
    return single ? 'single_day_weekly_repeat' : 'date_range_consecutive';
  };

  const validate = () => {
    if (!advertiserId) return 'Choose a client (advertiser).';
    if (!items.length) return 'Add at least one item.';
    for (const it of items) {
      if (!it.product_type) return 'Every item needs a product type.';
      if (it.category === 'digital_edition' && !(it.edition_ids?.length)) return `Choose at least one edition for "${it.product_type}".`;
      if (it.category === 'website' && !it.digital_start_date) return `Choose a start date for "${it.product_type}".`;
    }
    return '';
  };

  const handleSubmit = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setError('');
    setSaving(true);
    try {
      const orderIsTaxable = !!orderTaxable;
      const orderTax = orderIsTaxable ? (Number(orderTaxRate) || 0) : 0;
      const payload = {
        advertiser_id: advertiserId,
        sales_rep_id: salesRepId === 'none' ? null : salesRepId,
        discount_type: discountType,
        discount_value: Number(discountValue) || 0,
        notes: notes || null,
        is_taxable: orderIsTaxable,
        tax_rate: orderTax,
        needs_factura: !!orderFactura,
        items: items.map(it => {
          const pt = ptByName(it.product_type);
          const single = !!pt?.single_day_product;
          return {
            category: it.category,
            product_type: it.product_type,
            edition_ids: it.category === 'digital_edition' ? it.edition_ids : null,
            rate: Number(it.rate) || 0,
            quantity: Math.max(1, Number(it.quantity) || 1),
            is_taxable: orderIsTaxable,
            tax_rate: orderTax,
            needs_factura: !!orderFactura,
            digital_start_date: it.category === 'website' ? it.digital_start_date : null,
            digital_end_date: it.category === 'website' ? (single ? it.digital_start_date : (it.digital_end_date || null)) : null,
            batch_schedule_type: buildScheduleType(it),
            repeat_every_weeks: Math.max(1, Number(it.repeat_every_weeks) || 1),
            special_instructions: it.special_instructions || null,
            package_id: it.package_id,
            package_name: it.package_name,
          };
        }),
      };
      await api.post('/orders', payload);
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to create order');
    } finally {
      setSaving(false);
    }
  };

  const sortedEditions = useMemo(() => [...(editions || [])].sort((a, b) => (b.publish_date || '').localeCompare(a.publish_date || '')), [editions]);
  const advName = advertisers.find(a => a.id === advertiserId)?.business_name;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0F0F0F] border-[#1E293B] text-[#F8FAFC] max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="order-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Newspaper className="w-5 h-5 text-[#3B82F6]" /> New Order</DialogTitle>
        </DialogHeader>

        {/* Client + rep */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Client *</Label>
            <Popover open={advOpen} onOpenChange={setAdvOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-full justify-between bg-[#121212] border-[#1E293B] text-[#F8FAFC] font-normal" data-testid="order-advertiser-trigger">
                  {advName || 'Select client'}
                  <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-[#121212] border-[#1E293B]">
                <Command className="bg-[#121212]">
                  <CommandInput placeholder="Search clients..." className="text-[#F8FAFC]" data-testid="order-advertiser-search" />
                  <CommandList>
                    <CommandEmpty>No client found.</CommandEmpty>
                    <CommandGroup>
                      {[...advertisers].sort((a, b) => a.business_name.localeCompare(b.business_name)).map(adv => (
                        <CommandItem key={adv.id} value={adv.business_name} onSelect={() => { setAdvertiserId(adv.id); setAdvOpen(false); }} className="text-[#F8FAFC]" data-testid={`order-adv-option-${adv.id}`}>
                          <Check className={`mr-2 h-4 w-4 ${advertiserId === adv.id ? 'opacity-100' : 'opacity-0'}`} />
                          {adv.business_name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Sales Rep</Label>
            <Select value={salesRepId} onValueChange={setSalesRepId}>
              <SelectTrigger className="bg-[#121212] border-[#1E293B] text-[#F8FAFC]" data-testid="order-sales-rep-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC]">
                <SelectItem value="none">Unassigned</SelectItem>
                {salesReps.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Items */}
        <div className="space-y-3 mt-2">
          <div className="flex items-center justify-between">
            <Label className="text-[#F8FAFC] text-base">Items ({items.length})</Label>
            <div className="flex items-center gap-2">
              {packages.length > 0 && (
                <Select value="" onValueChange={addPackage}>
                  <SelectTrigger className="h-9 bg-[#121212] border-[#3B82F6]/40 text-[#3B82F6] w-[190px]" data-testid="order-add-package-select">
                    <div className="flex items-center gap-1.5"><PackageIcon className="w-4 h-4" /><SelectValue placeholder="Add a package" /></div>
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC]">
                    {packages.map(p => <SelectItem key={p.id} value={p.id} data-testid={`order-package-opt-${p.id}`}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Button type="button" onClick={addItem} variant="outline" className="h-9 border-[#1E293B] bg-[#121212] text-[#F8FAFC]" data-testid="order-add-item-btn"><Plus className="w-4 h-4 mr-1" /> Add Item</Button>
            </div>
          </div>

          {items.map((it, idx) => {
            const pt = ptByName(it.product_type);
            const single = !!pt?.single_day_product;
            const productsForCat = (productTypes || []).filter(p => p.category === it.category);
            return (
              <div key={it._uid} className="border border-[#1E293B] rounded-lg p-3 bg-[#121212] space-y-3" data-testid={`order-item-${idx}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-[#94A3B8]">
                    {it.category === 'website' ? <Globe className="w-4 h-4 text-[#3B82F6]" /> : <Newspaper className="w-4 h-4 text-[#3B82F6]" />}
                    <span>Item {idx + 1}</span>
                    {it.package_name && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-[#3B82F6]/15 text-[#3B82F6]"><PackageIcon className="w-3 h-3" />{it.package_name}</span>}
                  </div>
                  <button type="button" onClick={() => removeItem(it._uid)} className="text-red-400 hover:text-red-300 p-1" data-testid={`order-remove-item-${idx}`}><Trash2 className="w-4 h-4" /></button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="space-y-1">
                    <Label className="text-[#94A3B8] text-xs">Type</Label>
                    <Select value={it.category} onValueChange={(v) => onCategoryChange(it._uid, v)}>
                      <SelectTrigger className="h-9 bg-[#0F0F0F] border-[#1E293B] text-[#F8FAFC]" data-testid={`order-item-category-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC]">
                        {categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1 col-span-1 md:col-span-2">
                    <Label className="text-[#94A3B8] text-xs">Product</Label>
                    <Select value={it.product_type} onValueChange={(v) => onProductChange(it._uid, v)}>
                      <SelectTrigger className="h-9 bg-[#0F0F0F] border-[#1E293B] text-[#F8FAFC]" data-testid={`order-item-product-${idx}`}><SelectValue placeholder="Select product" /></SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC]">
                        {productsForCat.map(p => <SelectItem key={p.id} value={p.name}>{p.name} — {formatCurrency(p.rate)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[#94A3B8] text-xs">Rate (each)</Label>
                    <Input type="number" value={it.rate} onChange={(e) => updateItem(it._uid, { rate: e.target.value })} className="h-9 bg-[#0F0F0F] border-[#1E293B] text-[#F8FAFC]" data-testid={`order-item-rate-${idx}`} />
                  </div>
                </div>

                {/* Category-specific */}
                {it.category === 'digital_edition' && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="space-y-1 col-span-2 md:col-span-3">
                      <Label className="text-[#94A3B8] text-xs">Editions *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-between h-9 bg-[#0F0F0F] border-[#1E293B] text-[#F8FAFC] font-normal" data-testid={`order-item-editions-${idx}`}>
                            {it.edition_ids.length ? `${it.edition_ids.length} edition(s)` : 'Select editions'}
                            <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[320px] p-2 bg-[#121212] border-[#1E293B] max-h-64 overflow-y-auto">
                          {sortedEditions.map(ed => {
                            const checked = it.edition_ids.includes(ed.id);
                            return (
                              <button key={ed.id} type="button" onClick={() => {
                                const next = checked ? it.edition_ids.filter(x => x !== ed.id) : [...it.edition_ids, ed.id];
                                updateItem(it._uid, { edition_ids: next });
                              }} className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#1E293B] text-left" data-testid={`order-edition-opt-${idx}-${ed.id}`}>
                                <Check className={`w-4 h-4 ${checked ? 'text-[#3B82F6]' : 'text-transparent'}`} />
                                <span className="text-sm text-[#F8FAFC] flex-1 truncate">{ed.name}</span>
                                <span className="text-xs text-[#475569]">{ed.publish_date}</span>
                              </button>
                            );
                          })}
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[#94A3B8] text-xs">Per edition</Label>
                      <Input type="number" min={1} value={it.quantity} onChange={(e) => updateItem(it._uid, { quantity: e.target.value })} className="h-9 bg-[#0F0F0F] border-[#1E293B] text-[#F8FAFC]" data-testid={`order-item-qty-${idx}`} />
                    </div>
                  </div>
                )}

                {it.category === 'website' && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <Label className="text-[#94A3B8] text-xs">{single ? 'Date *' : 'Start date *'}</Label>
                      <Input type="date" value={it.digital_start_date} onChange={(e) => updateItem(it._uid, { digital_start_date: e.target.value })} className="h-9 bg-[#0F0F0F] border-[#1E293B] text-[#F8FAFC]" data-testid={`order-item-start-${idx}`} />
                    </div>
                    {!single && (
                      <div className="space-y-1">
                        <Label className="text-[#94A3B8] text-xs">End date</Label>
                        <Input type="date" value={it.digital_end_date} onChange={(e) => updateItem(it._uid, { digital_end_date: e.target.value })} className="h-9 bg-[#0F0F0F] border-[#1E293B] text-[#F8FAFC]" data-testid={`order-item-end-${idx}`} />
                      </div>
                    )}
                    <div className="space-y-1">
                      <Label className="text-[#94A3B8] text-xs">Quantity</Label>
                      <Input type="number" min={1} value={it.quantity} onChange={(e) => updateItem(it._uid, { quantity: e.target.value })} className="h-9 bg-[#0F0F0F] border-[#1E293B] text-[#F8FAFC]" data-testid={`order-item-qty-${idx}`} />
                    </div>
                    {single && (it.quantity || 1) > 1 && (
                      <div className="space-y-1">
                        <Label className="text-[#94A3B8] text-xs">Every N weeks</Label>
                        <Input type="number" min={1} value={it.repeat_every_weeks} onChange={(e) => updateItem(it._uid, { repeat_every_weeks: e.target.value })} className="h-9 bg-[#0F0F0F] border-[#1E293B] text-[#F8FAFC]" data-testid={`order-item-weeks-${idx}`} />
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-end pt-1">
                  <div className="text-sm text-[#94A3B8]">
                    {lineCount(it)} × {formatCurrency(it.rate || 0)} = <span className="text-[#F8FAFC] font-medium" data-testid={`order-item-subtotal-${idx}`}>{formatCurrency(lineSubtotal(it))}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Discount + notes + summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div className="space-y-3">
            <div className="space-y-2">
              <Label className="text-[#94A3B8] flex items-center gap-1.5"><Tag className="w-4 h-4" /> Order Discount</Label>
              <div className="flex gap-2">
                <Select value={discountType} onValueChange={setDiscountType}>
                  <SelectTrigger className="h-9 bg-[#121212] border-[#1E293B] text-[#F8FAFC] w-40" data-testid="order-discount-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC]">
                    <SelectItem value="none">No discount</SelectItem>
                    <SelectItem value="percent">Percentage %</SelectItem>
                    <SelectItem value="amount">Fixed amount $</SelectItem>
                  </SelectContent>
                </Select>
                {discountType !== 'none' && (
                  <Input type="number" min={0} value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} className="h-9 bg-[#121212] border-[#1E293B] text-[#F8FAFC]" placeholder={discountType === 'percent' ? '10' : '200'} data-testid="order-discount-value" />
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Tax &amp; Factura <span className="text-[10px] text-[#475569]">(applies to the whole order)</span></Label>
              <div className="flex flex-wrap items-center gap-4 bg-[#121212] border border-[#1E293B] rounded-md px-3 py-2">
                <label className="flex items-center gap-2 text-sm text-[#94A3B8]">
                  <Switch checked={orderTaxable} onCheckedChange={setOrderTaxable} data-testid="order-taxable" /> Taxable
                </label>
                {orderTaxable && (
                  <div className="flex items-center gap-1.5">
                    <Input type="number" value={orderTaxRate} onChange={(e) => setOrderTaxRate(e.target.value)} className="h-8 w-20 bg-[#0F0F0F] border-[#1E293B] text-[#F8FAFC]" data-testid="order-tax-rate" />
                    <span className="text-xs text-[#94A3B8]">% tax</span>
                  </div>
                )}
                <label className="flex items-center gap-2 text-sm text-[#94A3B8]">
                  <Switch checked={orderFactura} onCheckedChange={setOrderFactura} data-testid="order-factura" /> Factura
                </label>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Notes</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-[#121212] border-[#1E293B] text-[#F8FAFC]" rows={2} data-testid="order-notes" />
            </div>
          </div>

          <div className="bg-[#121212] border border-[#1E293B] rounded-lg p-4 space-y-2 self-start">
            <div className="flex justify-between text-sm"><span className="text-[#94A3B8]">Subtotal</span><span className="text-[#F8FAFC]" data-testid="order-subtotal">{formatCurrency(subtotal)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-[#94A3B8]">Discount</span><span className="text-emerald-400" data-testid="order-discount-amount">− {formatCurrency(discountAmount)}</span></div>
            <div className="h-px bg-[#1E293B] my-1" />
            <div className="flex justify-between text-base font-semibold"><span className="text-[#F8FAFC]">Net{orderTaxable ? ' (before tax)' : ''}</span><span className="text-[#3B82F6]" data-testid="order-total">{formatCurrency(total)}</span></div>
            {orderTaxable && (
              <>
                <div className="flex justify-between text-sm"><span className="text-[#94A3B8]">Tax ({Number(orderTaxRate) || 0}%)</span><span className="text-[#F8FAFC]" data-testid="order-tax-amount">{formatCurrency(taxAmount)}</span></div>
                <div className="flex justify-between text-lg font-bold"><span className="text-[#F8FAFC]">Grand total</span><span className="text-[#3B82F6]" data-testid="order-grand-total">{formatCurrency(grandTotal)}</span></div>
              </>
            )}
            <p className="text-[10px] text-[#475569] pt-1">Discount is spread across items{orderTaxable ? '; tax is applied on the invoice.' : '. Turn on Taxable to add tax.'}{orderFactura ? ' Factura requested.' : ''}</p>
          </div>
        </div>

        {error && <div className="text-sm text-red-400 bg-red-900/20 border border-red-800 rounded-md p-2" data-testid="order-error">{error}</div>}

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose} className="border-[#1E293B] text-[#94A3B8]">Cancel</Button>
          <Button type="button" disabled={saving} onClick={handleSubmit} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="order-submit-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : `Create Order (${formatCurrency(total)})`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrderModal;
